import { isFeatureSwitchEnabled } from "@/helpers/switch.helpers";
import type { NavRouteTree } from "./types";

/**
 * Config for all navigation routes.
 * Routes, Nav menus and permissions are all derived from this config.
 */
const navRouteTree: NavRouteTree = [
  {
    path: "/profile",
    label: "Profile",
    isExternal: true,
    srcPath: "/profile",
    exclude: true,
    roles: ["admin", "trader", "asset", "viewer", "finance"],
    breadCrumbHandledExternally: true,
  },
  {
    path: "/home",
    label: "Home",
    icon: "HomeIcon",
    isExternal: true,
    srcPath: "/home",
    roles: ["admin", "trader", "asset", "viewer", "finance"],
    breadCrumbHandledExternally: true,
  },
  {
    path: "/settings/home",
    label: "Home Settings",
    exclude: true,
    isExternal: true,
    srcPath: "/settings/home",
    roles: ["admin"],
    breadCrumbHandledExternally: true,
  },
  {
    path: "/dashboards",
    label: "Dashboards",
    icon: "DashboardIcon",
    isExternal: true,
    srcPath: "/dashboards",
    roles: ["admin", "trader", "asset", "viewer"],
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/portfolio-overview",
    label: "Portfolio Overview",
    icon: "AssessmentIcon",
    isExternal: true,
    srcPath: "/microservice/portfolio-overview/web/",
    roles: ["admin", "trader", "asset"],
  },
  {
    path: "/explorer",
    label: "Explorer",
    icon: "AutoGraphIcon",
    isExternal: false,
    roles: ["admin", "trader", "asset"],
  },
  {
    label: "Trading",
    icon: "CurrencyExchangeIcon",
    roles: ["admin", "trader", "asset", "viewer"],
    children: [
      {
        path: "/trading/trade-hub",
        label: "Optimiser Hub",
        icon: "AccountBalanceIcon",
        isExternal: false,
        isBeta: true,
        roles: ["admin", "trader", "asset"],
        exclude: !isFeatureSwitchEnabled("optimiser-hub"),
      },

      {
        path: "/trading/optimisation",
        label: "Optimiser Configuration",
        icon: "SettingsSuggestIcon",
        isExternal: true,
        srcPath: "/microservice/optimiser-ui/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/portfolio-control",
        label: "Portfolio Control",
        icon: "TuneIcon",
        isExternal: true,
        srcPath: "/microservice/portfolio-control/web/",
        roles: ["admin", "trader"],
      },
      {
        path: "/trading/imbalance",
        label: "Imbalance Price Prediction",
        icon: "PolymerIcon",
        isExternal: true,
        srcPath: "/microservice/prediction/web/imbalance_price",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/m7gc",
        label: "M7 Gate Closure Price Prediction",
        icon: "ShowChartIcon",
        isExternal: true,
        srcPath: "/microservice/prediction/web/m7_gate_price_mean",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/wholesale-automation",
        label: "Wholesale Automation",
        icon: "AccountBalanceIcon",
        isExternal: true,
        srcPath: "/microservice/trade-automation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/bm-automation",
        label: "BM Automation",
        icon: "EqualizerIcon",
        isExternal: true,
        srcPath: "/microservice/bm-automation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/bm-inputs",
        label: "BM Inputs",
        icon: "VerticalAlignCenterIcon",
        isExternal: true,
        srcPath: "/microservice/bm-input/web/",
        roles: ["admin", "trader", "asset"],
      },
      {
        path: "/trading/ancillary-services",
        label: "Ancillary Services Automation",
        icon: "GraphicEqIcon",
        isExternal: true,
        srcPath: "/microservice/gb-dynamic-automation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/melmil",
        label: "MEL/MIL Automation",
        icon: "ImportExportIcon",
        isExternal: true,
        srcPath: "/microservice/melmil-automation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/ptimetabling",
        label: "Parameter Timetabling",
        icon: "UpdateIcon",
        isExternal: true,
        srcPath: "/microservice/parameter-timetabling/web/",
        roles: ["admin", "trader", "viewer"],
      },
    ],
  },
  {
    label: "Markets",
    icon: "SsidChartIcon",
    roles: ["admin", "trader", "viewer"],
    children: [
      {
        path: "/markets/bm-instructions",
        label: "BM Instructions",
        icon: "EqualizerIcon",
        isExternal: false,
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/markets/melmil",
        label: "MEL/MIL Exchange",
        icon: "ImportExportIcon",
        isExternal: true,
        srcPath: "/microservice/melmil-exchange/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/markets/bod",
        label: "Bid Offer Data",
        icon: "VerticalAlignCenterIcon",
        isExternal: true,
        srcPath: "/microservice/bod-exchange/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/markets/pn",
        label: "Physical Notifications",
        icon: "LabelIcon",
        isExternal: true,
        srcPath: "/microservice/pn-exchange/web/",
        roles: ["admin", "trader", "viewer"],
      },

      {
        path: "/markets/m7",
        label: "EPEX Intraday Market",
        icon: "AccountBalanceIcon",
        isExternal: true,
        srcPath: "/microservice/m7-exchange/web/",
        roles: ["admin", "trader", "viewer"],
      },
    ],
  },
  {
    label: "Scheduling",
    icon: "ScheduleIcon",
    roles: ["admin", "trader", "asset", "viewer"],
    children: [
      {
        path: "/scheduling/power",
        label: "Power",
        icon: "PowerIcon",
        isExternal: false,
        roles: ["admin", "trader", "asset", "viewer"],
      },
      {
        path: "/scheduling/energy",
        label: "Energy",
        icon: "BatteryChargingFullIcon",
        isExternal: false,
        roles: ["admin", "trader", "asset", "viewer"],
      },
      {
        path: "/scheduling/asset-availability",
        label: "Asset Availability",
        icon: "PowerSettingsNewIcon",
        isExternal: true,
        srcPath: "/microservice/schedules-visualization/web/",
        roles: ["admin", "trader", "asset", "viewer"],
      },
      {
        path: "/scheduling/availability-planning",
        label: "Availability Planning",
        icon: "EventIcon",
        isExternal: true,
        srcPath: "/microservice/availability-planning/web/",
        roles: ["admin", "trader", "asset"],
      },
    ],
  },
  {
    label: "Reporting",
    icon: "AssignmentIcon",
    roles: ["admin", "trader", "asset", "finance"],
    children: [
      {
        path: "/reporting/trade-blotter",
        label: "Trade Blotter",
        icon: "GridOnIcon",
        isExternal: true,
        srcPath: "/microservice/trade-blotter/web/",
        roles: ["admin", "trader", "finance"],
      },
      {
        path: "/reporting/data-exporter",
        label: "Data Exporter",
        icon: "DescriptionIcon",
        isExternal: true,
        srcPath: "/microservice/data-portal/web/",
        roles: ["admin", "trader", "asset", "finance"],
      },
    ],
  },
  {
    label: "Simulators",
    icon: "DonutSmallIcon",
    roles: ["admin", "asset", "trader", "viewer"],
    excludedEnvs: ["prod"],
    children: [
      {
        path: "/simulation/pagerduty",
        label: "PagerDuty",
        icon: "WarningIcon",
        isExternal: true,
        srcPath: "/microservice/pagerduty-simulator/web/",
        roles: ["admin"],
      },
      {
        path: "/simulation/eso",
        label: "ESO",
        icon: "SupervisorAccountIcon",
        isExternal: true,
        srcPath: "/microservice/eso-simulation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/simulation/ets",
        label: "EPEX DAM",
        icon: "GroupWorkIcon",
        isExternal: true,
        srcPath: "/microservice/ets-simulation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/simulation/asset1",
        label: "SimulationAsset1",
        icon: "BatteryFullIcon",
        isExternal: true,
        srcPath: "/microservice/asset1-simulation/web/",
        roles: ["admin", "asset", "trader", "viewer"],
      },
      {
        path: "/simulation/asset2",
        label: "Asset2",
        icon: "BatteryFullIcon",
        isExternal: true,
        srcPath: "/microservice/asset2-simulation/web/",
        roles: ["admin", "asset", "trader", "viewer"],
      },
      {
        path: "/simulation/asset3",
        label: "Asset3",
        icon: "BatteryFullIcon",
        isExternal: true,
        srcPath: "/microservice/asset3-simulation/web/",
        roles: ["admin", "asset", "trader", "viewer"],
      },
    ],
  },
  {
    label: "Administration",
    icon: "AccountBoxIcon",
    roles: ["admin"],
    children: [
      {
        path: "/administration/fault",
        label: "Fault",
        icon: "ErrorOutlineIcon",
        isExternal: false,
        roles: ["admin"],
      },
    ],
  },
];

export default navRouteTree;
